/**
 * Application constants including breakpoints, API endpoints, cache TTL values, and role definitions
 */

// Responsive breakpoints (in pixels)
export const BREAKPOINTS = {
  xs: 480,
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1600,
} as const;

export const MOBILE_BREAKPOINT = BREAKPOINTS.md;

// API endpoints
export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: '/api/Auth/login',
    ME: '/api/Auth/me',
  },
  // Students
  STUDENT: {
    BASE: '/api/Student',
    BY_ID: (id: number) => `/api/Student/${id}`,
    STATUS: (id: number) => `/api/Student/${id}/status`,
  },
  // Payments
  PAYMENT: {
    BASE: '/api/Payment',
    BY_ID: (id: number) => `/api/Payment/${id}`,
    RECEIPT: (id: number) => `/api/Payment/${id}/receipt`,
    ARREARS: '/api/Payment/arrears',
  },
  // Attendance
  ATTENDANCE: {
    BASE: '/api/Attendance',
    SCAN: '/api/Attendance/scan',
    BY_DATE: (date: string) => `/api/Attendance/date/${date}`,
    STUDENT_HISTORY: (studentId: number) => `/api/Attendance/student/${studentId}`,
    REPORT: '/api/Attendance/report',
  },
  // Expenses
  EXPENSE: {
    BASE: '/api/Expense',
    BY_ID: (id: number) => `/api/Expense/${id}`,
    APPROVE: (id: number) => `/api/Expense/${id}/approve`,
    SUMMARY: '/api/Expense/summary',
  },
  // Reports
  REPORT: {
    DASHBOARD: '/api/Report/dashboard',
    FINANCIAL_DAILY: '/api/Report/financial/daily',
    FINANCIAL_MONTHLY: '/api/Report/financial/monthly',
  },
  // Users
  USER: {
    BASE: '/api/User',
    BY_ID: (id: number) => `/api/User/${id}`,
  },
  // Schools (SuperAdmin)
  SCHOOL: {
    BASE: '/api/School',
    BY_ID: (id: number) => `/api/School/${id}`,
  },
  // Billing (SuperAdmin)
  BILLING: {
    CONFIGURATION: '/api/billing/configuration',
    INVOICES: '/api/billing/invoices',
    RECEIPTS: '/api/billing/receipts',
  },
  // Reference data
  CLASS: {
    BASE: '/api/class',
    BY_ID: (id: number) => `/api/class/${id}`,
    BY_LEVEL: (levelId: number) => `/api/class/level/${levelId}`,
    WITH_STUDENTS: (id: number) => `/api/class/${id}/students`,
  },
  LEVEL: {
    BASE: '/api/level',
    BY_ID: (id: number) => `/api/level/${id}`,
  },
  ACADEMIC_YEAR: {
    BASE: '/api/academic-years',
    BY_ID: (id: number) => `/api/academic-years/${id}`,
    ACTIVATE: (id: number) => `/api/academic-years/${id}/activate`,
  },
  TERM: {
    BASE: '/api/terms',
    BY_ID: (id: number) => `/api/terms/${id}`,
    ACTIVATE: (id: number) => `/api/terms/${id}/activate`,
  },
  BANK_DEPOSITS: {
    BASE: '/api/bank-deposits',
  },
  // Advanced Accounting Module
  CHART_OF_ACCOUNTS: {
    BASE: '/api/chart-of-accounts',
    BY_ID: (id: number) => `/api/chart-of-accounts/${id}`,
    HIERARCHY: '/api/chart-of-accounts/hierarchy',
    BY_TYPE: (type: string) => `/api/chart-of-accounts/type/${type}`,
  },
  JOURNAL_ENTRY: {
    BASE: '/api/journal-entries',
    BY_ID: (id: number) => `/api/journal-entries/${id}`,
    POST: (id: number) => `/api/journal-entries/${id}/post`,
    REVERSE: (id: number) => `/api/journal-entries/${id}/reverse`,
    FROM_PAYMENT: (paymentId: number) => `/api/journal-entries/from-payment/${paymentId}`,
    FROM_EXPENSE: (expenseId: number) => `/api/journal-entries/from-expense/${expenseId}`,
  },
  GENERAL_LEDGER: {
    ACCOUNT: (accountId: number) => `/api/general-ledger/account/${accountId}`,
    ALL_BALANCES: '/api/general-ledger/balances',
  },
  BUDGET: {
    BASE: '/api/budgets',
    BY_ID: (id: number) => `/api/budgets/${id}`,
    APPROVE: (id: number) => `/api/budgets/${id}/approve`,
    CLOSE: (id: number) => `/api/budgets/${id}/close`,
    VS_ACTUAL: (id: number) => `/api/budgets/${id}/vs-actual`,
  },
  PAYROLL: {
    BASE: '/api/payroll',
    BY_ID: (id: number) => `/api/payroll/${id}`,
    POST: (id: number) => `/api/payroll/${id}/post`,
    ENTRIES: (id: number) => `/api/payroll/${id}/entries`,
    ENTRY: (id: number, entryId: number) => `/api/payroll/${id}/entries/${entryId}`,
    CALCULATE: '/api/payroll/calculate-deductions',
  },
  CREDITOR: {
    BASE: '/api/creditors',
    BY_ID: (id: number) => `/api/creditors/${id}`,
    INVOICES: '/api/creditors/invoices',
    INVOICE_BY_ID: (id: number) => `/api/creditors/invoices/${id}`,
    INVOICE_PAYMENT: (invoiceId: number) => `/api/creditors/invoices/${invoiceId}/payments`,
    HIRE_PURCHASES: '/api/creditors/hire-purchases',
    HIRE_PURCHASE_BY_ID: (id: number) => `/api/creditors/hire-purchases/${id}`,
    MARK_INSTALMENT_PAID: (hpId: number, instalmentId: number) =>
      `/api/creditors/hire-purchases/${hpId}/instalments/${instalmentId}/pay`,
  },
  ACCOUNTING_REPORTS: {
    TRIAL_BALANCE: '/api/accounting-reports/trial-balance',
    INCOME_STATEMENT: '/api/accounting-reports/income-statement',
    BALANCE_SHEET: '/api/accounting-reports/balance-sheet',
    CASH_FLOW: '/api/accounting-reports/cash-flow',
  },
} as const;

// Cache TTL values (in milliseconds)
export const CACHE_TTL = {
  STUDENT_LIST: 5 * 60 * 1000, // 5 minutes
  PAYMENT_LIST: 5 * 60 * 1000, // 5 minutes
  ATTENDANCE_LIST: 2 * 60 * 1000, // 2 minutes
  REFERENCE_DATA: 15 * 60 * 1000, // 15 minutes (classes, levels, terms)
  DASHBOARD: 1 * 60 * 1000, // 1 minute
} as const;

// User roles
export const USER_ROLES = {
  SUPER_ADMIN: 'SuperAdmin',
  SCHOOL_ADMIN: 'SchoolAdmin',
  PRINCIPAL: 'Principal',
  TEACHER: 'Teacher',
  FINANCE_OFFICER: 'FinanceOfficer',
  ACCOUNTS_CLERK: 'AccountsClerk',
  RECEPTIONIST: 'Receptionist',
  GUARD: 'Guard',
  PARENT: 'Parent',
  STUDENT: 'Student',
  FLEET_MANAGER: 'FleetManager',
  DRIVER: 'Driver',
} as const;

export type UserRole = typeof USER_ROLES[keyof typeof USER_ROLES];

// Payment methods
export const PAYMENT_METHODS = {
  CASH: 'Cash',
  MOBILE_MONEY: 'MobileMoney',
  BANK_TRANSFER: 'BankTransfer',
  ONLINE: 'Online',
} as const;

// Student status
export const STUDENT_STATUS = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
  GRADUATED: 'Graduated',
} as const;

// Attendance status
export const ATTENDANCE_STATUS = {
  PRESENT: 'Present',
  ABSENT: 'Absent',
  LATE: 'Late',
} as const;

// Expense status
export const EXPENSE_STATUS = {
  PENDING: 'Pending',
  APPROVED: 'Approved',
} as const;

// Scan types
export const SCAN_TYPES = {
  CHECK_IN: 'CheckIn',
  CHECK_OUT: 'CheckOut',
} as const;

// Gender options
export const GENDER_OPTIONS = {
  MALE: 'Male',
  FEMALE: 'Female',
} as const;

// Notification durations (in seconds)
export const NOTIFICATION_DURATION = {
  SUCCESS: 3,
  ERROR: 5,
  INFO: 4,
  WARNING: 4,
} as const;

// Form validation
export const VALIDATION = {
  PASSWORD_MIN_LENGTH: 6,
  PASSWORD_MIN_LENGTH_STRONG: 8,
  PHONE_MIN_LENGTH: 10,
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  DEBOUNCE_DELAY: 300, // milliseconds
  AUTO_REFRESH_INTERVAL: 5 * 60 * 1000, // 5 minutes (reduced from 1 minute to save data)
} as const;

// Expense approval threshold
export const EXPENSE_APPROVAL_THRESHOLD = 500;

// Late arrival time (8:00 AM)
export const LATE_ARRIVAL_TIME = '08:00:00';

// Arrears highlight threshold
export const ARREARS_THRESHOLD = -1000;
